import React from "react";
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  MouseSensor,
  useDroppable,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  horizontalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Paper, ListItem, ListItemText } from "@mui/material";

type Word = {
  id: string;
  text: string;
};

type SortableItemProps = {
  id: string;
  text: string;
};

const SortableItem: React.FC<SortableItemProps> = ({ id, text }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    padding: "8px",
    marginRight: "8px",
    backgroundColor: "#fff",
    border: "1px solid #ccc",
    cursor: "grab",
    display: "inline-block",
  };

  return (
    <Paper ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <ListItem>
        <ListItemText primary={text} />
      </ListItem>
    </Paper>
  );
};

type DropZoneProps = {
  sentence: Word[];
  setSentence: React.Dispatch<React.SetStateAction<Word[]>>;
  correctOrder: string[];
  activeId: string | null;
};

const DropZone: React.FC<DropZoneProps> = ({
  sentence,
  setSentence,
  correctOrder,
  activeId,
}) => {
  const { isOver, setNodeRef } = useDroppable({
    id: "words-drop-zone",
  });

  const isCorrectOrder =
    sentence.length > 0 &&
    sentence.every((word, index) => word.text === correctOrder[index]);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    })
  );

  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setSentence((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={sentence}
        strategy={horizontalListSortingStrategy}
      >
        <div
          ref={setNodeRef}
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            padding: "16px",
            border: `2px dashed ${
              sentence.length == 0 ? "gray" : isCorrectOrder ? "green" : "red"
            }`,
            minHeight: "100px",
            width: "100%",
            backgroundColor: isOver ? "#e0f7fa" : "#f5f5f5", // Change color when dragged over
            transition: "background-color 0.3s ease", // Smooth transition for background color
          }}
        >
          {sentence.map((item) => (
            <SortableItem key={item.id} id={item.id} text={item.text} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

export default DropZone;
